import { findClickThroughFromEvents, fillInEmptyTrackingTypes, mapCompanionBannerMetadata } from "../../../models/creatives";
import { TrackerType, filterOutEmptyTrackingUrls } from "../../../models/creatives/trackers";
import { ServiceStatus, GeoTargetsKeys, serviceToInventoryPackagesV2, inventoryPackagesToServiceV2, ServiceBrandSafetyLevels, BrandSafetyLevels, ServiceViewability, Viewability } from "../../../types";
import { satoshisToDollars, dollarsToSatoshis } from "../../../utils";
import { DealCategory } from "../../../models/deals";
import { CampaignStatus, isActionRequiredStatus, daypartToServiceDaypart, campaignStatusToServiceStatus, instructionToServiceInstruction, toDayPartsFromServiceDateParts, isLiveStatus, fromServiceOwners, serviceStatusToCampaignStatus, getUnresolvedStatusDetails } from "../instruction";
import { metadataToExternalMetadata, serviceExternalMetaToExternalMeta } from "../meta";
import { serviceOptimizationToOptimization } from "../optimization";
import { experimentExposuresFromService, experimentExposuresToService } from "../experimentExposures";
// Using a new default that doesn't put in a [""] as default b/c
// this isn't an url and the UI should be able to handle []
const DEFAULT_EMPTY_TRACKING_PIXELS = [
    {
        type: TrackerType.IMPRESSION,
        urls: []
    },
    {
        type: TrackerType.START,
        urls: []
    },
    {
        type: TrackerType.Q1,
        urls: []
    },
    {
        type: TrackerType.Q2,
        urls: []
    },
    {
        type: TrackerType.Q3,
        urls: []
    },
    {
        type: TrackerType.Q4,
        urls: []
    },
    {
        type: TrackerType.CLICK_TRACKER,
        urls: []
    },
    {
        type: TrackerType.CLICK_THROUGH,
        urls: []
    }
];
export var DerivedLineItemStatus;
(function (DerivedLineItemStatus) {
    DerivedLineItemStatus[DerivedLineItemStatus["INVALID"] = 0] = "INVALID";
    DerivedLineItemStatus[DerivedLineItemStatus["MISSING_CREATIVE"] = 1] = "MISSING_CREATIVE";
    DerivedLineItemStatus[DerivedLineItemStatus["MISSING_WHITELIST"] = 2] = "MISSING_WHITELIST";
    DerivedLineItemStatus[DerivedLineItemStatus["READY"] = 3] = "READY";
    DerivedLineItemStatus[DerivedLineItemStatus["WAITING"] = 4] = "WAITING";
    DerivedLineItemStatus[DerivedLineItemStatus["NO_ACTIVE_CREATIVE"] = 5] = "NO_ACTIVE_CREATIVE";
    DerivedLineItemStatus[DerivedLineItemStatus["LIVE"] = 6] = "LIVE";
    DerivedLineItemStatus[DerivedLineItemStatus["COMPLETED"] = 7] = "COMPLETED";
    DerivedLineItemStatus[DerivedLineItemStatus["PAUSED"] = 8] = "PAUSED";
    DerivedLineItemStatus[DerivedLineItemStatus["CANCELLED"] = 9] = "CANCELLED";
    DerivedLineItemStatus[DerivedLineItemStatus["ARCHIVED"] = 10] = "ARCHIVED";
    DerivedLineItemStatus[DerivedLineItemStatus["MISSING_GEO"] = 11] = "MISSING_GEO";
    DerivedLineItemStatus[DerivedLineItemStatus["MISSING_PRODUCT"] = 12] = "MISSING_PRODUCT";
    DerivedLineItemStatus[DerivedLineItemStatus["MISSING_GOAL"] = 13] = "MISSING_GOAL";
    DerivedLineItemStatus[DerivedLineItemStatus["MISSING_FREQ_CAP"] = 14] = "MISSING_FREQ_CAP";
    DerivedLineItemStatus[DerivedLineItemStatus["MISSING_DETAILS"] = 999] = "MISSING_DETAILS"; // has more than one invalid status (only used by statusDetails.summary)
})(DerivedLineItemStatus || (DerivedLineItemStatus = {}));
/**
 * Provides a single source of truth for default georecency values.
 */
export const createDefaultGeorecency = (overrides) => {
    return {
        circles: [],
        duration: undefined,
        ...overrides
    };
};
/**
 * Converts service geo fields to Georecency object.
 *
 * @param geoInclude - Service geo fields from API response
 * @returns Georecency object or undefined if no data is present
 */
export const serviceGeoFieldsToGeorecency = (geoInclude) => {
    const hasRecencyCircles = (geoInclude?.recency_circles?.length ?? 0) > 0;
    const shouldCreateGeorecency = hasRecencyCircles || !!geoInclude?.recency_duration;
    if (!shouldCreateGeorecency) {
        return undefined;
    }
    return createDefaultGeorecency({
        circles: geoInclude?.recency_circles ?? [],
        duration: geoInclude?.recency_duration ?? undefined
    });
};
/**
 * Converts a BrandSafetyLevels value to its corresponding ServiceBrandSafetyLevels value.
 * Used when sending data to the API.
 *
 * @param brandSafety - The client brand safety level to convert
 * @returns The corresponding service brand safety level, or undefined if the input is undefined or doesn't match any known level
 *
 * @example
 * ```typescript
 * const result = brandSafetyLevelsToService(BrandSafetyLevels.HIGH);
 * // Returns: ServiceBrandSafetyLevels.HIGH ("HIGH_PROTECTION")
 * ```
 */
export const brandSafetyLevelsToService = (brandSafety) => {
    switch (brandSafety) {
        case BrandSafetyLevels.NONE:
            return ServiceBrandSafetyLevels.NONE;
        case BrandSafetyLevels.MODERATE:
            return ServiceBrandSafetyLevels.MODERATE;
        case BrandSafetyLevels.HIGH:
            return ServiceBrandSafetyLevels.HIGH;
        default:
            return undefined;
    }
};
/**
 * Converts a ServiceBrandSafetyLevels value to its corresponding BrandSafetyLevels value.
 * Used when receiving data from the API.
 *
 * @param brandSafety - The service brand safety level to convert
 * @returns The corresponding client brand safety level, or undefined if the input is undefined or doesn't match any known level
 *
 * @example
 * ```typescript
 * const result = serviceToBrandSafetyLevels(ServiceBrandSafetyLevels.HIGH);
 * // Returns: BrandSafetyLevels.HIGH ("High Protection")
 * ```
 */
export const serviceToBrandSafetyLevels = (brandSafety) => {
    switch (brandSafety) {
        case ServiceBrandSafetyLevels.NONE:
            return BrandSafetyLevels.NONE;
        case ServiceBrandSafetyLevels.MODERATE:
            return BrandSafetyLevels.MODERATE;
        case ServiceBrandSafetyLevels.HIGH:
            return BrandSafetyLevels.HIGH;
        default:
            return undefined;
    }
};
/**
 * Converts a Viewability value to its corresponding ServiceViewability value.
 * Used when sending data to the API.
 *
 * @param viewability - The client viewability level to convert
 * @returns The corresponding service viewability level. Returns ANY_VIEWABILITY (0) when undefined.
 */
export const viewabilityToService = (viewability) => {
    switch (viewability) {
        case Viewability.FORTY_PLUS:
            return ServiceViewability.FORTY_PLUS;
        case Viewability.FIFTY_PLUS:
            return ServiceViewability.FIFTY_PLUS;
        case Viewability.SIXTY_PLUS:
            return ServiceViewability.SIXTY_PLUS;
        case Viewability.SEVENTY_PLUS:
            return ServiceViewability.SEVENTY_PLUS;
        default:
            return ServiceViewability.ANY_VIEWABILITY;
    }
};
/**
 * Converts a ServiceViewability value to its corresponding Viewability value.
 * Used when receiving data from the API.
 *
 * @param viewability - The service viewability level to convert
 * @returns The corresponding client viewability level, or undefined if ANY_VIEWABILITY (0) or unknown
 */
export const serviceToViewability = (viewability) => {
    switch (viewability) {
        case ServiceViewability.FORTY_PLUS:
            return Viewability.FORTY_PLUS;
        case ServiceViewability.FIFTY_PLUS:
            return Viewability.FIFTY_PLUS;
        case ServiceViewability.SIXTY_PLUS:
            return Viewability.SIXTY_PLUS;
        case ServiceViewability.SEVENTY_PLUS:
            return Viewability.SEVENTY_PLUS;
        case ServiceViewability.ANY_VIEWABILITY:
        default:
            return undefined;
    }
};
/**
 * Builds a LineItem from a Service Line Item
 * @param serviceLineitem
 * @returns
 */
export const serviceCampaignLineItemToCampaignLineItem = async (serviceLineitem, crypto) => {
    const { ad_positions, start_date, product_id, oms_id, name, end_date, start_asap, booked, external_id, budget, dayparts, frequencies, id, version, status: instructionStatus, geo_include, geo_exclude, derived_statuses, updated_by, publisher_group_id, parent_name, scenario_id, brand_safety_level, viewability } = serviceLineitem;
    const extMeta = serviceLineitem.ext_metas
        ? serviceExternalMetaToExternalMeta(serviceLineitem.ext_metas)
        : undefined;
    const startDate = new Date(start_date);
    const endDate = new Date(end_date);
    let creativesCount = 0;
    const attachedCreatives = [];
    const archivedCreatives = [];
    const creatives = [];
    if (serviceLineitem.creatives) {
        serviceLineitem.creatives.forEach((serviceCreativeConfig) => {
            const creativeConfig = {
                id: serviceCreativeConfig.id,
                creativeId: serviceCreativeConfig.creative_vid_id,
                endDate: new Date(serviceCreativeConfig.end.seconds * 1000),
                startDate: new Date(serviceCreativeConfig.start.seconds * 1000),
                weight: serviceCreativeConfig.weight / 100,
                clickThroughUrl: findClickThroughFromEvents(serviceCreativeConfig.event_urls),
                trackingPixels: serviceCreativeConfig.event_urls
                    ? fillInEmptyTrackingTypes(serviceCreativeConfig.event_urls, DEFAULT_EMPTY_TRACKING_PIXELS)
                    : DEFAULT_EMPTY_TRACKING_PIXELS,
                status: serviceCreativeConfig.status,
                startAsap: serviceCreativeConfig?.start_asap || false,
                flightName: serviceCreativeConfig?.flight_name,
                companionBannerIds: serviceCreativeConfig?.companion_banner_ids,
                companionBannerMetadata: mapCompanionBannerMetadata(serviceCreativeConfig?.companion_banner_metadata),
                mediaType: serviceCreativeConfig?.media_type
            };
            creatives.push(creativeConfig);
            if (creativeConfig.status !== ServiceStatus.ARCHIVED) {
                attachedCreatives.push(creativeConfig);
                creativesCount += 1;
            }
            if (creativeConfig.status === ServiceStatus.ARCHIVED) {
                archivedCreatives.push(creativeConfig);
            }
        });
    }
    const geoTargetingInclude = {
        country: geo_include?.country || "",
        districts: geo_include?.congressional_districts || [],
        dmas: geo_include?.dmas || [],
        regions: geo_include?.states || [],
        postalCodes: geo_include?.zips || [],
        circles: geo_include?.circles || []
    };
    const geoTargetingExclude = {
        country: geo_exclude?.country || "",
        districts: geo_exclude?.congressional_districts || [],
        dmas: geo_exclude?.dmas || [],
        regions: geo_exclude?.states || [],
        postalCodes: geo_exclude?.zips || [],
        circles: geo_exclude?.circles || []
    };
    const georecency = serviceGeoFieldsToGeorecency(geo_include);
    const statusText = getCampaignStatus(derived_statuses?.[0], instructionStatus);
    const { advertiserId, stationId, agencyId, stationGroupId, ownersWithTypes } = await fromServiceOwners(serviceLineitem, crypto);
    const brand_safety = serviceToBrandSafetyLevels(brand_safety_level);
    const viewabilityLevel = serviceToViewability(viewability);
    // inventory_mode isn't a first-class ServiceLineItem field — it rides in ext_metas
    // (see the `deals` doc comment on LineItemBase) since madserver has no dedicated column for it.
    const dealCategory = extMeta?.inventoryMode === "pg-deal" ? DealCategory.PG_DEAL : DealCategory.PRODUCT_ONLY;
    const inventoryPackagesV2 = serviceLineitem.inventory_packages_v2
        ? serviceToInventoryPackagesV2(serviceLineitem.inventory_packages_v2)
        : undefined;
    // The saved package never carries its own id (see lineItemToServiceCampaignLineItem) —
    // inventoryIds[0] is the selected deal's id.
    const deals = dealCategory === DealCategory.PG_DEAL && inventoryPackagesV2?.[0]
        ? { ...inventoryPackagesV2[0], id: inventoryPackagesV2[0].inventoryIds?.[0] }
        : undefined;
    const lineItem = {
        id,
        adPositions: ad_positions,
        budget: satoshisToDollars(budget || 0),
        bookedImpressions: booked,
        archivedCreatives,
        attachedCreatives,
        creativesCount,
        dayparts: toDayPartsFromServiceDateParts(dayparts),
        frequencies: frequencies || [],
        endDate,
        startDate,
        startAsap: start_asap || false,
        name,
        mediaType: serviceLineitem.media_type,
        productId: product_id || "",
        externalOrderManagementSystemId: oms_id,
        externalId: external_id,
        instructionStatus,
        lastUpdated: serviceLineitem.updated ? new Date(serviceLineitem.updated) : undefined,
        version,
        geoTargeting: {
            exclude: geoTargetingExclude,
            include: geoTargetingInclude
        },
        georecency,
        confirmedHealthcare: serviceLineitem.confirmed_healthcare ?? undefined,
        confirmedDataUsage: serviceLineitem.confirmed_data_usage ?? undefined,
        confirmedRegionalRestrictions: serviceLineitem.confirmed_regional_restrictions ?? undefined,
        confirmedSensitiveCategories: serviceLineitem.confirmed_sensitive_categories ?? undefined,
        confirmedDeliveryImpact: serviceLineitem.confirmed_delivery_impact ?? undefined,
        meta: extMeta,
        status: statusText,
        criticalStatuses: getCriticalStatuses(serviceLineitem),
        effectiveCpm: satoshisToDollars(serviceLineitem.ecpm),
        parent: serviceLineitem.parent,
        parentName: parent_name,
        advertiserId,
        stationId,
        agencyId,
        stationGroupId,
        ownersWithTypes,
        actualizedImpressions: serviceLineitem.actualized,
        segments: serviceLineitem.segments || [],
        deviceCaps: {
            tv: serviceLineitem.device_caps?.tv ? serviceLineitem.device_caps.tv / 100 : 0,
            desktop: serviceLineitem.device_caps?.desktop ? serviceLineitem.device_caps.desktop / 100 : 0,
            tablet: serviceLineitem.device_caps?.tablet ? serviceLineitem.device_caps.tablet / 100 : 0,
            mobile: serviceLineitem.device_caps?.mobile ? serviceLineitem.device_caps.mobile / 100 : 0
        },
        get isEditable() {
            return (statusText !== CampaignStatus.ARCHIVED &&
                !!startDate &&
                !!endDate &&
                startDate.valueOf() < endDate.valueOf());
        },
        updatedBy: updated_by,
        publisherGroupId: publisher_group_id,
        targetingRules: serviceLineitem.targeting_rules,
        scenarioId: scenario_id,
        isLive() {
            return isLiveStatus(this.status);
        },
        iab_category_rtb_ids: serviceLineitem.iab_category_rtb_ids,
        // A `fields` projection can omit `status_details` - fall back to a neutral status instead
        statusDetails: serviceLineitem.status_details
            ? {
                code: serviceLineitem.status_details.code,
                ...(serviceLineitem.status_details.invalid_reasons?.length
                    ? {
                        invalidReasons: serviceLineitem.status_details.invalid_reasons.map(
                        /* @ts-expect-error - (TS Upgrade: 5.7.3) - https://typescript.tv/errors/#ts7053 */
                        (reason) => CampaignStatus[DerivedLineItemStatus[reason]])
                    }
                    : {}),
                /* @ts-expect-error - (TS Upgrade: 5.7.3) - https://typescript.tv/errors/#ts7053 */
                summary: CampaignStatus[DerivedLineItemStatus[serviceLineitem.status_details.summary]],
                canBe: serviceLineitem.status_details.canBe
            }
            : getUnresolvedStatusDetails(),
        goalInterval: serviceLineitem.goal_interval,
        tpa: serviceLineitem.tpa,
        tpa_id: serviceLineitem.tpa_id,
        inventoryPackagesV2,
        dealCategory,
        deals,
        optimizationTemplate: serviceLineitem.optimization_template,
        contextualSegmentIncludes: serviceLineitem.contextual_segment_includes,
        contextualSegmentExcludes: serviceLineitem.contextual_segment_excludes,
        contextualTargeting: serviceLineitem.contextual_targeting,
        contextualAvoidance: serviceLineitem.contextual_avoidance,
        keywordTargeting: serviceLineitem.keyword_targeting,
        keywordAvoidance: serviceLineitem.keyword_avoidance,
        brandSafetyLevel: brand_safety,
        viewability: viewabilityLevel,
        optimization: serviceOptimizationToOptimization(serviceLineitem.optimization),
        experimentExposures: experimentExposuresFromService(serviceLineitem.experiment_exposures)
    };
    return lineItem;
};
/**
 * Takes a geoTargeting and returns the need format for service targeting
 * @param geoTargeting The geoTargeting to be convereted
 * @returns undefined if no targeting given (service doesn't support empty values) or
 * the correct targeting for a service.
 *
 * Validation logic on the service side of for geo targeting
 * is a bit wild so here is the current state of it
 * 1. Country is required if no DMAs or Districts
 * 2. Country is required if Zipcodes
 * 3. Country is required if Regions (states)
 * 4. If DMA then no country
 * 5. If DMA and Zipcode Or Regions then Country
 * 6. If District then no country
 * 7. If District and Zipcode Or Regions then Country
 */
export const geoTargetsToServiceGeoFields = (geoTargeting) => {
    if (!geoTargeting) {
        return undefined;
    }
    // skip conversion if all geotargeting fields are empty
    if (geoTargeting.country === "" &&
        geoTargeting.regions.length === 0 &&
        geoTargeting.districts.length === 0 &&
        geoTargeting.dmas.length === 0 &&
        geoTargeting.postalCodes.length === 0 &&
        (!geoTargeting.circles || geoTargeting.circles.length === 0)) {
        return undefined;
    }
    return {
        country: geoTargeting.country,
        states: geoTargeting.regions,
        congressional_districts: geoTargeting.districts,
        dmas: geoTargeting.dmas,
        zips: geoTargeting.postalCodes,
        circles: geoTargeting.circles || []
    };
};
/**
 * Converts line item geo targeting and georecency data to service geo fields format.
 *
 * Handles the merge of standard geo targeting (include/exclude) with georecency-specific
 * fields (recency circles, duration).
 *
 * Note: Georecency is only considered when geoTargeting is present (intentional business logic).
 *
 * @param geoTargeting - Line item geography targeting details (include/exclude)
 * @param georecency - Line item georecency data (circles, duration)
 * @returns Service geo fields with geo_include and geo_exclude, or empty object if no geoTargeting
 */
export const lineItemGeoToServiceGeoFields = (geoTargeting, georecency) => {
    if (!geoTargeting) {
        return {};
    }
    const geoInclude = geoTargetsToServiceGeoFields(geoTargeting.include);
    const georecencyFields = georecency
        ? {
            recency_circles: georecency.circles || [],
            recency_duration: georecency.duration ?? undefined
        }
        : undefined;
    return {
        geo_include: geoInclude || georecencyFields ? { ...geoInclude, ...georecencyFields } : undefined,
        geo_exclude: geoTargetsToServiceGeoFields(geoTargeting.exclude)
    };
};
/**
 * @param lineItem: a service variant of a line item.
 * @return: an array of the critical (action required) statuses associated with it.
 */
export const getCriticalStatuses = (lineItem) => {
    const critical = new Set();
    for (const derived of lineItem.derived_statuses || []) {
        const status = getCampaignStatus(derived, lineItem.status);
        isActionRequiredStatus(status) && critical.add(status);
    }
    return Array.from(critical.values());
};
/**
 * @param derivedStatus: a derived status, from a service line item (or undefined, if there is none).
 * @param instructionStatus: a service status of a line item.
 * @return: the equivalent campaign status text, as far as it can be deduced.
 */
export const getCampaignStatus = (derivedStatus, instructionStatus) => {
    if (derivedStatus) {
        const derived = DerivedLineItemStatus[derivedStatus];
        /* @ts-expect-error - (TS Upgrade: 5.7.3) - https://typescript.tv/errors/#ts7053 */
        const status = derived ? CampaignStatus[derived] : null;
        if (status) {
            return status;
        }
    }
    return serviceStatusToCampaignStatus(instructionStatus);
};
/**
 * Statuses for which a past start date must be preserved on save: READY, PAUSED
 * and CANCELLED are locked by madserver's validStartTimeChange, while ARCHIVED
 * and EXPIRED saves skip that validation entirely, so re-resolving ASAP would
 * silently rewrite the historical start date. Drafts and partials without a
 * status (create flows) may still re-resolve.
 */
const START_DATE_LOCKED_STATUSES = new Set([
    ServiceStatus.READY,
    ServiceStatus.PAUSED,
    ServiceStatus.CANCELLED,
    ServiceStatus.ARCHIVED,
    ServiceStatus.EXPIRED
]);
/**
 * True when the resource has already started delivering: its stored start date
 * is in the past and it is (or once was) live. Once started, the backend rejects
 * any start date change, so ASAP must not re-resolve to "now" on edits (SUPPORT-9913).
 */
const hasStarted = (status, startDate) => !!startDate &&
    startDate.getTime() < Date.now() &&
    status !== undefined &&
    START_DATE_LOCKED_STATUSES.has(status);
/**
 * ASAP may only resolve to "now" when that can produce a valid date range.
 * If the end date is already in the past (e.g. a campaign duplicated from an
 * ended one keeps the source's flight dates), start = now would violate
 * start < end and be rejected by madserver's inst_check/inst_creative_check
 * constraints, so the stored start date is preserved until the user moves the
 * end date into the future. When there is no stored start date at all
 * (ASAP allows omitting it), there is nothing to preserve and ASAP must
 * supply the value regardless of the end date.
 */
const canResolveAsapToNow = (status, startDate, endDate) => {
    if (!startDate) {
        return true;
    }
    return !hasStarted(status, startDate) && (!endDate || endDate.getTime() > Date.now());
};
/**
 * Targeting-tab values, plus a Product-mode budget, are preserved in FE state for
 * toggle-back, so they must be cleared here for PG Deal saves — a PG line item is
 * priced from its deal via `bookedImpressions`, and madserver rejects a payload
 * where both `budget` and `booked` are positive.
 */
const clearTargetingFieldsForPgDeal = (lineItem) => {
    if (lineItem.dealCategory !== DealCategory.PG_DEAL) {
        return {};
    }
    return {
        ...("budget" in lineItem && { budget: 0 }),
        ...("geoTargeting" in lineItem && { geo_include: null, geo_exclude: null }),
        ...("dayparts" in lineItem && { dayparts: null }),
        ...("deviceCaps" in lineItem && { device_caps: null }),
        ...("frequencies" in lineItem && { frequencies: [] }),
        ...("adPositions" in lineItem && { ad_positions: [] }),
        ...("productId" in lineItem && { product_id: null }),
        ...("publisherGroupId" in lineItem && { whitelist_id: null }),
        ...("confirmedHealthcare" in lineItem && { confirmed_healthcare: null }),
        ...("confirmedDataUsage" in lineItem && { confirmed_data_usage: null }),
        ...("confirmedRegionalRestrictions" in lineItem && {
            confirmed_regional_restrictions: null
        }),
        ...("confirmedSensitiveCategories" in lineItem && {
            confirmed_sensitive_categories: null
        }),
        ...("confirmedDeliveryImpact" in lineItem && { confirmed_delivery_impact: null }),
        ...("segments" in lineItem && { segments: null }),
        ...("contextualTargeting" in lineItem && { contextual_targeting: [] }),
        ...("contextualAvoidance" in lineItem && { contextual_avoidance: [] }),
        ...("contextualSegmentIncludes" in lineItem && { contextual_segment_includes: [] }),
        ...("contextualSegmentExcludes" in lineItem && { contextual_segment_excludes: [] }),
        ...("keywordTargeting" in lineItem && { keyword_targeting: [] }),
        ...("keywordAvoidance" in lineItem && { keyword_avoidance: [] }),
        ...("viewability" in lineItem && { viewability: ServiceViewability.ANY_VIEWABILITY })
    };
};
/**
 * Takes a lineItem and converts it into a Service Line Item
 * @param lineItem Line item to be converted.
 * @returns a formated service line item.
 */
export const lineItemToServiceCampaignLineItem = (lineItem) => {
    const base = instructionToServiceInstruction(lineItem);
    const brandSafety = brandSafetyLevelsToService(lineItem.brandSafetyLevel);
    // inventory_mode has no dedicated ServiceLineItem column — it's stored in ext_metas so a
    // saved line item's Product/PG Deal mode survives a reload (see the `dealCategory` doc
    // comment on LineItemBase).
    const inventoryMode = "dealCategory" in lineItem
        ? lineItem.dealCategory === DealCategory.PG_DEAL
            ? "pg-deal"
            : "product"
        : undefined;
    const startDate = lineItem.startAsap &&
        canResolveAsapToNow(lineItem.instructionStatus, lineItem.startDate, lineItem.endDate)
        ? new Date()
        : lineItem.startDate;
    const serviceLineItem = {
        ...base,
        ...(("startDate" in lineItem || "startAsap" in lineItem) && {
            start_date: startDate?.toISOString()
        }),
        ...("endDate" in lineItem && { end_date: lineItem.endDate?.toISOString() }),
        ...("startAsap" in lineItem && { start_asap: lineItem.startAsap || false }),
        ...("effectiveCpm" in lineItem && { ecpm: dollarsToSatoshis(lineItem.effectiveCpm) || 0 }),
        ...("actualizedImpressions" in lineItem && { actualized: lineItem.actualizedImpressions || 0 }),
        ...("status" in lineItem && {
            status: campaignStatusToServiceStatus(lineItem.status, lineItem.instructionStatus)
        }),
        ...("productEcpm" in lineItem && { product_ecpm: lineItem.productEcpm }),
        ...("attachedCreatives" in lineItem && {
            creatives: lineItem.attachedCreatives?.map((item) => {
                const event_urls = filterOutEmptyTrackingUrls(item.trackingPixels)
                    .filter((el) => el.type !== 100)
                    .map((el) => {
                    return {
                        type: el.type,
                        urls: el.urls.map((url) => url.trim())
                    };
                });
                if (item.clickThroughUrl?.trim()?.length) {
                    event_urls.push({ type: 100, urls: [item.clickThroughUrl.trim()] });
                }
                return {
                    id: item.id,
                    parent: lineItem.id || "",
                    creative_vid_id: item.creativeId,
                    start_asap: item.startAsap || false,
                    start: {
                        seconds: item.startAsap && canResolveAsapToNow(item.status, item.startDate, item.endDate)
                            ? Math.floor(new Date().getTime() / 1000)
                            : Math.floor(item.startDate.getTime() / 1000)
                    },
                    end: { seconds: Math.floor(item.endDate.getTime() / 1000) },
                    weight: Math.floor(item.weight * 100),
                    event_urls,
                    status: item.status,
                    ...(item.flightName !== undefined && {
                        flight_name: item.flightName
                    }),
                    ...(item.companionBannerIds !== undefined && {
                        companion_banner_ids: item.companionBannerIds
                    })
                };
            })
        }),
        ...(("meta" in lineItem || inventoryMode !== undefined) && {
            ext_metas: metadataToExternalMetadata({
                ...lineItem.meta,
                ...(inventoryMode !== undefined && { inventoryMode })
            })
        }),
        ...("targetingRules" in lineItem && { targeting_rules: lineItem.targetingRules }),
        ...("scenarioId" in lineItem && { scenario_id: lineItem.scenarioId }),
        ...("tpa_id" in lineItem && { tpa_id: lineItem.tpa_id }),
        ...("tpa" in lineItem && { tpa: lineItem.tpa }),
        ...("iab_category_rtb_ids" in lineItem && {
            iab_category_rtb_ids: lineItem.iab_category_rtb_ids
        }),
        // A PG Deal line item's packages come from `deals`, not `inventoryPackagesV2`
        // (see the `deals` doc comment on LineItemBase).
        ...(("inventoryPackagesV2" in lineItem || "deals" in lineItem) && {
            inventory_packages_v2: lineItem.dealCategory === DealCategory.PG_DEAL
                ? // The package has no id of its own — `deals.id` is the selected deal's id,
                    // used to populate `inventory_ids`, not a package id the backend should track.
                    inventoryPackagesToServiceV2(lineItem.deals ? [lineItem.deals] : []).map(({ id: _id, ...rest }) => rest)
                : inventoryPackagesToServiceV2(lineItem.inventoryPackagesV2)
        }),
        ...("optimizationTemplate" in lineItem && {
            optimization_template: lineItem.optimizationTemplate
        }),
        ...("brandSafetyLevel" in lineItem && {
            brand_safety_level: brandSafety
        }),
        ...("experimentExposures" in lineItem && {
            experiment_exposures: experimentExposuresToService(lineItem.experimentExposures)
        })
    };
    // PG Deal saves clear Targeting-tab fields instead of computing them, since the
    // tab isn't shown for PG Deal line items and the values would just be discarded.
    if (lineItem.dealCategory === DealCategory.PG_DEAL) {
        Object.assign(serviceLineItem, clearTargetingFieldsForPgDeal(lineItem));
    }
    else {
        const geoTargeting = "geoTargeting" in lineItem ? lineItem.geoTargeting : undefined;
        const georecency = "georecency" in lineItem ? lineItem.georecency : undefined;
        const viewabilityService = viewabilityToService(lineItem.viewability);
        Object.assign(serviceLineItem, {
            ...("adPositions" in lineItem && { ad_positions: lineItem.adPositions || [] }),
            ...("dayparts" in lineItem && { dayparts: daypartToServiceDaypart(lineItem.dayparts) }),
            ...(geoTargeting && lineItemGeoToServiceGeoFields(geoTargeting, georecency)),
            ...("confirmedHealthcare" in lineItem && {
                confirmed_healthcare: lineItem.confirmedHealthcare ?? undefined
            }),
            ...("confirmedDataUsage" in lineItem && {
                confirmed_data_usage: lineItem.confirmedDataUsage ?? undefined
            }),
            ...("confirmedRegionalRestrictions" in lineItem && {
                confirmed_regional_restrictions: lineItem.confirmedRegionalRestrictions ?? undefined
            }),
            ...("confirmedSensitiveCategories" in lineItem && {
                confirmed_sensitive_categories: lineItem.confirmedSensitiveCategories ?? undefined
            }),
            ...("confirmedDeliveryImpact" in lineItem && {
                confirmed_delivery_impact: lineItem.confirmedDeliveryImpact ?? undefined
            }),
            ...("segments" in lineItem && { segments: lineItem.segments || [] }),
            ...("productId" in lineItem && { product_id: lineItem.productId }),
            ...("publisherGroupId" in lineItem && { whitelist_id: lineItem.publisherGroupId }),
            ...("deviceCaps" in lineItem && {
                device_caps: {
                    tv: lineItem.deviceCaps?.tv ? lineItem.deviceCaps.tv * 100 : 0,
                    desktop: lineItem.deviceCaps?.desktop ? lineItem.deviceCaps.desktop * 100 : 0,
                    tablet: lineItem.deviceCaps?.tablet ? lineItem.deviceCaps.tablet * 100 : 0,
                    mobile: lineItem.deviceCaps?.mobile ? lineItem.deviceCaps.mobile * 100 : 0
                }
            }),
            ...("contextualSegmentIncludes" in lineItem && {
                contextual_segment_includes: lineItem.contextualSegmentIncludes
            }),
            ...("contextualSegmentExcludes" in lineItem && {
                contextual_segment_excludes: lineItem.contextualSegmentExcludes
            }),
            ...("contextualTargeting" in lineItem && {
                contextual_targeting: lineItem.contextualTargeting
            }),
            ...("contextualAvoidance" in lineItem && {
                contextual_avoidance: lineItem.contextualAvoidance
            }),
            ...("keywordTargeting" in lineItem && {
                keyword_targeting: lineItem.keywordTargeting
            }),
            ...("keywordAvoidance" in lineItem && {
                keyword_avoidance: lineItem.keywordAvoidance
            }),
            ...("viewability" in lineItem && {
                viewability: viewabilityService
            })
        });
    }
    return serviceLineItem;
};
export const lineItemToBulkServiceLineItem = (lineItem) => {
    const serviceLineItem = lineItemToServiceCampaignLineItem(lineItem);
    // bulkLineItem endpoint uses `dayparts_include` instead of `dayparts`.
    serviceLineItem.dayparts_include = serviceLineItem.dayparts;
    delete serviceLineItem.dayparts;
    // bulkLineItem endpoint uses `recipes` instead of `segments`.
    serviceLineItem.recipes = serviceLineItem.segments;
    delete serviceLineItem.segments;
    return serviceLineItem;
};
/**
 * @param item: anything.
 * @return: type narrowed. True if the item is a line item. Else, false.
 */
export const isLineItem = (item) => {
    return (typeof item === "object" &&
        item !== null &&
        "creativesCount" in item &&
        "id" in item &&
        "instructionStatus" in item &&
        "attachedCreatives" in item &&
        "geoTargeting" in item &&
        "endDate" in item &&
        "startDate" in item);
};
export const DSP_OPTIONS = [
    { id: "0LDY6rsTok3wcPtS29QFr97Mtpw7", label: "Madhive" },
    { id: "NaYohTJvHtJVMSHZW0CIhepYt5Xi", label: "Google Ad Manager ( GAM )" },
    { id: "3SLsQgGodlAmVrUmZYoQ1GcZoMDj", label: "Xandr" }
];
//# sourceMappingURL=index.js.map